/**
 * use-panel-refresh — Lot 2 du plan UI/UX.
 *
 * Hook unique pour exposer un état "live / polling / stale / error" lisible
 * par tous les panels. Trois modes possibles, parfois combinés :
 *
 *   - **query** : panel qui pull via TanStack (createToolQuery). Le hook
 *     observe `isFetching` + `dataUpdatedAt` et expose `refetch()`.
 *
 *   - **subscription** : panel qui push via `usePanelSubscription` (live/panel/
 *     data avec `topic`). Le hook lit son `loading` + `lastUpdate` et bascule
 *     en mode "live" tant que des frames arrivent.
 *
 *   - **liveSignal** : panel qui gère ses propres WS listeners (`ws.on`,
 *     comme process.tsx avec son envelope `live/process/watch.*`). Le panel
 *     passe deux accesseurs : `active` (vrai quand l'abonnement est en
 *     marche côté agent) + `lastUpdate` (timestamp ms du dernier push WS,
 *     bumpé par le panel à chaque message). Le hook calcule "live" / "stale"
 *     selon la fraîcheur.
 *
 * En l'absence des trois, le hook reste utilisable (mode "manual") — utile
 * pour signaler que la liste a été chargée mais que rien ne la rafraîchit.
 *
 * Les "id de signalement" (jobId pour `live/plugin/job.event`, topic pour
 * `live/panel/data`) restent gérés par les hooks WS dédiés (`useJobEvents`,
 * `usePanelSubscription`) — ce hook n'est qu'une couche de présentation.
 */
import { createSignal, createMemo, onCleanup, type Accessor } from "solid-js";
import { useWs } from "./ws";

export type RefreshMode = "live" | "polling" | "manual" | "stale" | "error";

export interface RefreshState {
  mode: RefreshMode;
  /** Vrai quand un fetch est en cours. */
  isFetching: boolean;
  /** Timestamp ms de la dernière donnée reçue (Date.now()). */
  lastUpdate: number | null;
  /** Déclenche un refetch manuel. No-op si pas de query attachée. */
  refetch: () => void | Promise<unknown>;
}

interface QueryLike {
  isFetching: boolean;
  refetch: () => Promise<unknown>;
  dataUpdatedAt?: number;
  error?: unknown;
}

interface SubscriptionLike {
  loading: Accessor<boolean>;
  lastUpdate: Accessor<number | null>;
}

interface LiveSignal {
  /** Vrai quand l'abonnement WS est actif (le panel reçoit des pushs). */
  active: Accessor<boolean>;
  /** Timestamp ms du dernier push WS, bumpé par le panel. */
  lastUpdate: Accessor<number | null>;
  /** Optionnel : seuil au-delà duquel on considère le flux périmé. Défaut 15 s. */
  staleAfterMs?: number;
}

export interface UsePanelRefreshOpts {
  query?: () => QueryLike;
  subscription?: SubscriptionLike;
  liveSignal?: LiveSignal;
  /** Intervalle de polling auto en ms. 0 = désactivé. */
  intervalMs?: number;
}

export function usePanelRefresh(opts: UsePanelRefreshOpts): Accessor<RefreshState> {
  const ws = useWs();
  const [tick, setTick] = createSignal(0);

  // Auto-refetch toutes les intervalMs millisecondes si une query est fournie.
  if (opts.intervalMs && opts.intervalMs > 0 && opts.query) {
    const id = setInterval(() => {
      void opts.query!().refetch();
      setTick((t) => t + 1);
    }, opts.intervalMs);
    onCleanup(() => clearInterval(id));
  }

  // Pour rafraîchir le mode "stale" automatiquement : tick chaque seconde si
  // un liveSignal est attaché (sinon le mode "live → stale" ne se déclenche
  // que sur une autre interaction réactive).
  if (opts.liveSignal) {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    onCleanup(() => clearInterval(id));
  }

  return createMemo<RefreshState>(() => {
    tick();
    const q = opts.query?.();
    const sub = opts.subscription;
    const live = opts.liveSignal;

    const fetching = (q?.isFetching ?? false) || (sub ? sub.loading() : false);

    // Priorité : liveSignal.lastUpdate > subscription.lastUpdate > query.dataUpdatedAt
    const lastUpdate =
      live?.lastUpdate() ?? sub?.lastUpdate() ?? (q?.dataUpdatedAt && q.dataUpdatedAt > 0 ? q.dataUpdatedAt : null);

    // La WS globale prime : si elle est tombée, aucune fraîcheur ne tient.
    // wsStatus vient du contexte WsProvider (shared/ws.tsx).
    const wsDown = ws.state.wsStatus !== "open" && ws.state.wsStatus !== "connecting";

    let mode: RefreshMode = "manual";
    if (q?.error) mode = "error";
    else if (wsDown && (live || sub)) mode = "error";
    else if (live) {
      const staleAfter = live.staleAfterMs ?? 15_000;
      const ts = live.lastUpdate();
      if (!live.active()) mode = "stale";
      else if (ts && Date.now() - ts > staleAfter) mode = "stale";
      else mode = "live";
    } else if (sub) {
      mode = sub.loading() ? "polling" : "live";
    } else if (opts.intervalMs && opts.intervalMs > 0) {
      mode = "polling";
    } else if (lastUpdate && Date.now() - lastUpdate > 60_000) {
      mode = "stale";
    }

    const refetch = () => q?.refetch() ?? undefined;

    return { mode, isFetching: fetching, lastUpdate, refetch };
  });
}
